import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { DASHBOARD_STAT_PERMISSIONS } from "../../constants/permissions";
import { usePermissions } from "../../hooks/usePermissions";
import { getDashboardStatsRequest } from "../../services/dashboardApi";

function StatCard({ label, value, isLoading }) {
  return (
    <article className="ui-stat-block stat rounded-2xl border border-base-300 bg-base-100 p-5 shadow-sm">
      <div className="stat-title">{label}</div>
      {isLoading ? (
        <div className="skeleton mt-3 h-9 w-16 rounded-lg" />
      ) : (
        <div className="stat-value text-primary">{value}</div>
      )}
    </article>
  );
}

const STAT_CONFIG = [
  {
    key: "users",
    label: "Total Users",
    permission: DASHBOARD_STAT_PERMISSIONS.users,
    apiKey: "total_users",
  },
  {
    key: "projects",
    label: "Total Projects",
    permission: DASHBOARD_STAT_PERMISSIONS.projects,
    apiKey: "total_projects",
  },
  {
    key: "vendors",
    label: "Total Vendors",
    permission: DASHBOARD_STAT_PERMISSIONS.vendors,
    apiKey: "total_vendors",
  },
];

function normalizeStatsPayload(payload) {
  const data = payload?.data ?? payload ?? {};
  return {
    total_users: Number(data.total_users) || 0,
    total_projects: Number(data.total_projects) || 0,
    total_vendors: Number(data.total_vendors) || 0,
  };
}

function DashboardPage() {
  const { can } = usePermissions();
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total_users: 0,
    total_projects: 0,
    total_vendors: 0,
  });

  const visibleStats = useMemo(
    () => STAT_CONFIG.filter((stat) => can(stat.permission)),
    [can],
  );

  useEffect(() => {
    let cancelled = false;

    const loadStats = async () => {
      if (!visibleStats.length) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const response = await getDashboardStatsRequest();
        if (cancelled) return;
        setStats(normalizeStatsPayload(response.data));
      } catch (error) {
        if (!cancelled) {
          toast.error(error.response?.data?.message || "Failed to load dashboard stats.");
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadStats();
    return () => {
      cancelled = true;
    };
  }, [visibleStats]);

  const cards = visibleStats.map((stat) => ({
    label: stat.label,
    value: stats[stat.apiKey],
  }));

  return (
    <main className="w-full min-w-0 bg-base-100">
      <section className="w-full border-b border-base-200 bg-base-100">
        <div className="border-b border-base-200 px-4 py-8 sm:px-8">
          <h1 className="ui-page-title tracking-tight">Dashboard</h1>
          <p className="ui-muted mt-2">Overview of your workspace.</p>
        </div>

        <div className="px-4 py-6 sm:px-8">
          {cards.length > 0 ? (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {cards.map((card) => (
                <StatCard
                  key={card.label}
                  label={card.label}
                  value={card.value}
                  isLoading={isLoading}
                />
              ))}
            </div>
          ) : (
            <div className="alert rounded-xl bg-base-200/70">
              <span className="ui-nav-text">No dashboard metrics available for your account.</span>
            </div>
          )}
        </div>
      </section>
    </main>
  );
}

export default DashboardPage;
